import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { Dialog, DialogOverlay, DialogPortal, DialogTitle } from "../../../components/ui/dialog";
import { Textarea } from "../../../components/ui/textarea";
import { cn } from "../../../lib/utils";
import {
  ACTION_REQUIRES_REASON,
  APPROVER_ACTION,
} from "../constants/approvalConstants";

/**
 * The confirmation step behind Screen 6's three actions.
 *
 * One modal for all three because the only thing that varies between them is
 * whether the reason is mandatory — and that comes from ACTION_REQUIRES_REASON
 * rather than from three near-identical components. Reject and Send Back
 * cannot be confirmed with an empty reason (VR-22); Approve takes an optional
 * comment.
 *
 * The modal never decides what an action does. It collects a reason, enforces
 * that rule, and hands both back to the screen.
 */

const COPY = {
  [APPROVER_ACTION.APPROVE]: {
    title: "conveyanceApproval.approveTitle",
    body: "conveyanceApproval.approveBody",
    reasonLabel: "conveyanceApproval.commentOptional",
    placeholder: "conveyanceApproval.commentPlaceholder",
    confirm: "conveyanceApproval.approve",
    pending: "conveyanceApproval.approving",
    tone: "primary",
  },
  [APPROVER_ACTION.REJECT]: {
    title: "conveyanceApproval.rejectTitle",
    body: "conveyanceApproval.rejectBody",
    reasonLabel: "conveyanceApproval.reasonRequired",
    placeholder: "conveyanceApproval.rejectReasonPlaceholder",
    confirm: "conveyanceApproval.reject",
    pending: "conveyanceApproval.rejecting",
    tone: "danger",
  },
  [APPROVER_ACTION.SEND_BACK]: {
    title: "conveyanceApproval.sendBackTitle",
    body: "conveyanceApproval.sendBackBody",
    reasonLabel: "conveyanceApproval.reasonRequired",
    placeholder: "conveyanceApproval.sendBackReasonPlaceholder",
    confirm: "conveyanceApproval.sendBack",
    pending: "conveyanceApproval.sendingBack",
    tone: "warning",
  },
  [APPROVER_ACTION.MARK_PROCESSED]: {
    title: "conveyanceApproval.markProcessedTitle",
    body: "conveyanceApproval.markProcessedBody",
    reasonLabel: null,
    placeholder: null,
    confirm: "conveyanceApproval.markProcessed",
    pending: "conveyanceApproval.processing",
    tone: "primary",
  },
};

const TONE_CLASS = {
  primary:
    "bg-[var(--color-primary)] hover:bg-[var(--color-accent)] shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)]",
  danger: "bg-rose-600 hover:bg-rose-700 shadow-[0_8px_20px_-8px_rgba(190,18,60,0.5)]",
  warning: "bg-amber-600 hover:bg-amber-700 shadow-[0_8px_20px_-8px_rgba(180,83,9,0.5)]",
};

export function ApproverActionModal({
  action,
  open,
  onOpenChange,
  onConfirm,
  summary = null,
  loading = false,
  error = "",
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    if (!open) {
      setReason("");
      setValidationError("");
    }
  }, [open]);

  if (!action) return null;

  const copy = COPY[action];
  const reasonMandatory = ACTION_REQUIRES_REASON[action];
  const collectsReason = Boolean(copy.reasonLabel);

  const handleConfirm = () => {
    const trimmed = reason.trim();
    if (reasonMandatory && !trimmed) {
      setValidationError(t("conveyanceApproval.errorReasonRequired"));
      return;
    }
    setValidationError("");
    onConfirm(trimmed);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="bg-[#0a1230]/50 backdrop-blur-sm" />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]",
            "w-[95vw] sm:w-[90vw] md:w-[500px] max-w-[500px]",
            "max-h-[90vh] overflow-hidden flex flex-col",
            "bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)] border border-white/60",
            "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "font-sans"
          )}
        >
          <div className="flex items-center justify-between px-6 py-3.5 border-b border-[var(--color-text-border)]/70 shrink-0">
            <DialogTitle className="text-[18px] font-semibold text-foreground tracking-tight">
              {t(copy.title)}
            </DialogTitle>
            <DialogPrimitive.Close
              disabled={loading}
              className="h-9 w-9 grid place-items-center rounded-xl text-[var(--color-text-secondary)] hover:bg-[var(--color-workspace)]/60 hover:text-[var(--color-primary)] transition disabled:opacity-50"
            >
              <X className="h-5 w-5" />
              <span className="sr-only">{t("formActions.close")}</span>
            </DialogPrimitive.Close>
          </div>

          <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1">
            <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">
              {t(copy.body)}
            </p>

            {/* Whatever the screen wants the approver to re-read before
                confirming — the claim it is about, or the batch size. */}
            {summary ? (
              <div className="rounded-2xl border border-[var(--color-text-border)]/40 bg-[var(--color-workspace)]/40 p-4 text-sm">
                {summary}
              </div>
            ) : null}

            {collectsReason ? (
              <div className="space-y-2">
                <label
                  htmlFor="approver-action-reason"
                  className="text-sm font-semibold text-foreground flex items-center gap-1"
                >
                  {t(copy.reasonLabel)}
                  {reasonMandatory ? <span className="text-rose-500">*</span> : null}
                </label>
                <Textarea
                  id="approver-action-reason"
                  rows={3}
                  value={reason}
                  disabled={loading}
                  placeholder={t(copy.placeholder)}
                  onChange={(event) => {
                    setReason(event.target.value);
                    if (validationError) setValidationError("");
                  }}
                  className="min-h-[0] rounded-xl border-[var(--color-text-border)] resize-none text-sm focus-visible:ring-[var(--color-accent)] focus-visible:border-[var(--color-accent)]"
                />
                {validationError ? (
                  <p className="text-sm font-medium text-rose-600">{validationError}</p>
                ) : null}
              </div>
            ) : null}

            {error ? <p className="text-sm font-medium text-rose-600">{error}</p> : null}
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 px-6 py-4 border-t border-[var(--color-text-border)]/70 shrink-0">
            <button
              type="button"
              disabled={loading}
              onClick={() => onOpenChange(false)}
              className="h-11 px-5 rounded-xl border border-[var(--color-text-border)] text-sm font-semibold text-foreground hover:bg-gray-50 transition cursor-pointer disabled:opacity-50"
            >
              {t("formActions.cancel")}
            </button>
            <button
              type="button"
              disabled={loading}
              onClick={handleConfirm}
              className={cn(
                "h-11 px-6 rounded-xl text-white text-sm font-semibold transition cursor-pointer flex items-center justify-center",
                TONE_CLASS[copy.tone],
                loading && "opacity-50 cursor-not-allowed shadow-none"
              )}
            >
              {loading ? t(copy.pending) : t(copy.confirm)}
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}

export default ApproverActionModal;
